// Honest deficit-target math for the US SMS plan-preview card.
//
// Client mirror of server/src/lib/plan-offer.ts `targetBandText` (kept in sync
// with the deficit/surplus bands in server/src/lib/plan-prompts.ts). The
// delivered MMS plan card's hero number is the DEFICIT daily target
// (recommended − deficit, clamped at the sex floor) — NOT raw
// tdee_recommended. The preview must show that honest band, never the higher
// maintenance number, or it promises a figure the card won't deliver.
//
//   lose:     rec − 500 … rec − 250   (clamped at the sex floor)
//   recomp:   rec − 250 … rec − 150   (clamped at the sex floor)
//   gain:     rec + 200 … rec + 350
//   maintain: rec
//
// The web app hands US users off to the SMS coach BEFORE the plan is generated,
// so the single daily target the EC2 plan-card ultimately renders (an
// LLM choice within this band) is not knowable here — we show the band
// midpoint as a single `~`-prefixed number (matches the real card's
// single-number hero; the `~` keeps it honest). GLP-1 users stay numberless: for them
// tdee_recommended is a theoretical maintenance ceiling, not a daily target
// (see the GLP-1 branch in server decomposition.ts / plan-offer.ts), so a hard
// calorie figure would contradict the card they actually receive.
//
// Honest-rate floor (2026-07): when the calc payload carries `honest_plan`
// (lose intent), the SERVER's numbers win over this client mirror:
//   adjusted       → the hero is the clamped floor target (target_kcal), a
//                    single number — never a band midpoint below it
//   activity_first → the hero is the maintenance eating anchor with its own
//                    label; the deficit-band math is skipped entirely
//   unchanged      → band as before, but the low end still clamps at the
//                    server's usable floor (max(BMR, sex minimum))

import { getStrings, type Lang } from '../i18n'
import type { HonestPlan } from './types'

export type PlanSex = 'male' | 'female' | null
export type PlanIntent = 'lose' | 'maintain' | 'recomp' | 'gain'

const FEMALE_FLOOR = 1200
const MALE_FLOOR = 1500

export type PlanTargetHero =
  // Numeric daily target — a single `~`-prefixed representative value (band
  // midpoint for deficit/surplus intents, ~rec for maintain).
  | { kind: 'band'; label: string; value: string; unit: string }
  // Honest-rate activity-first (lose intent, no meaningful deficit at safe
  // calories): the maintenance eating anchor, with movement as the lever.
  | { kind: 'activity'; label: string; value: string; unit: string }
  // GLP-1 cohort — numberless protein-floor framing (matches the real card).
  | { kind: 'glp1' }
  // No tdeeRecommended available (legacy/edge) — numberless fallback.
  | { kind: 'none' }

function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US')
}

export function planTargetHero(opts: {
  tdeeRecommended: number | null
  intent?: PlanIntent
  sex: PlanSex
  isGlp1?: boolean
  honestPlan?: HonestPlan | null
  lang?: Lang
}): PlanTargetHero {
  if (opts.isGlp1) return { kind: 'glp1' }
  if (opts.tdeeRecommended == null || !Number.isFinite(opts.tdeeRecommended)) {
    return { kind: 'none' }
  }
  const t = getStrings(opts.lang ?? 'en')
  const rec = Math.round(opts.tdeeRecommended)
  // Unknown sex (legacy random token, no slug) → female floor (the lower of
  // the two) so we never clamp a target ABOVE what the card would deliver.
  const floor = opts.sex === 'male' ? MALE_FLOOR : FEMALE_FLOOR
  const unit = t.cta.unitPerDay
  const intent = opts.intent ?? 'lose'

  if (intent === 'maintain') {
    return { kind: 'band', label: t.cta.heroMaintainLabel, value: `~${fmt(rec)}`, unit }
  }

  // Honest-rate floor (lose intent, server-computed): the server's numbers
  // win over the client mirror below.
  const honest = intent === 'lose' ? opts.honestPlan ?? null : null
  if (honest?.mode === 'activity_first') {
    return {
      kind: 'activity',
      label: t.cta.heroActivityLabel,
      value: `~${fmt(honest.target_kcal ?? rec)}`,
      unit,
    }
  }
  if (honest?.mode === 'adjusted' && honest.target_kcal !== null) {
    // The plan's target IS the safety floor — show it exactly, not a midpoint
    // of a band that dips below it.
    return { kind: 'band', label: t.cta.heroDailyLabel, value: `~${fmt(honest.target_kcal)}`, unit }
  }

  let low: number
  let high: number
  if (intent === 'recomp') {
    low = Math.max(floor, rec - 250)
    high = Math.max(low, rec - 150)
  } else if (intent === 'gain') {
    low = rec + 200
    high = rec + 350
  } else {
    // lose (default). When the server sent honest numbers (mode unchanged),
    // its usable floor — max(BMR, sex minimum) — clamps the band's low end.
    const effectiveFloor = honest ? Math.max(floor, honest.usable_floor_kcal) : floor
    low = Math.max(effectiveFloor, rec - 500)
    high = Math.max(low, rec - 250)
  }
  // Single representative daily target = band midpoint, prefixed `~`. Matches
  // the real card's single-number hero for punch, while the `~` stays honest:
  // the EC2 card's exact value is an LLM choice within [low, high], so the
  // midpoint can differ from the delivered number by up to half the band width.
  const mid = Math.round((low + high) / 2)
  return { kind: 'band', label: t.cta.heroDailyLabel, value: `~${fmt(mid)}`, unit }
}
